const BASE = '/browse';

export interface EntitySummary {
  key: string;
  display: string;
  count: number;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_count: number | null;
}

export interface PaginatedResponse {
  entity: string;
  display: string;
  total: number;
  page: number;
  per_page: number;
  pages: number;
  sort: string;
  order: string;
  data: Record<string, unknown>[];
}

export interface RecordDetail {
  entity: string;
  record: Record<string, unknown>;
  joins: JoinInfo[];
}

export interface JoinInfo {
  key: string;
  display: string;
  to_entity: string;
  count: number;
}

export interface JoinResult {
  join: string;
  display: string;
  from_entity: string;
  to_entity: string;
  source_id: number;
  total: number;
  page: number;
  per_page: number;
  pages: number;
  data: Record<string, unknown>[];
}

export interface GlobalSearchResult {
  query: string;
  results: Record<string, {
    display: string;
    label_column: string;
    count: number;
    data: Record<string, unknown>[];
  }>;
}

export interface JoinPath {
  key: string;
  display: string;
  from_entity: string;
  to_entity: string;
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return res.json();
}

export function fetchEntities(): Promise<{ entities: EntitySummary[] }> {
  return fetchJson(`${BASE}/entities`);
}

export function fetchEntityList(
  entity: string,
  params: { q?: string; sort?: string; order?: string; page?: number; per_page?: number } = {},
): Promise<PaginatedResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  if (params.page) qs.set('page', String(params.page));
  if (params.per_page) qs.set('per_page', String(params.per_page));
  const suffix = qs.toString() ? `?${qs}` : '';
  return fetchJson(`${BASE}/${entity}${suffix}`);
}

export function fetchRecord(entity: string, id: number): Promise<RecordDetail> {
  return fetchJson(`${BASE}/${entity}/${id}`);
}

export function globalSearch(q: string, limit = 10): Promise<GlobalSearchResult> {
  return fetchJson(`${BASE}/search/global?q=${encodeURIComponent(q)}&limit=${limit}`);
}

export function fetchJoin(
  joinKey: string,
  id: number,
  params: { page?: number; per_page?: number } = {},
): Promise<JoinResult> {
  const qs = new URLSearchParams({ id: String(id) });
  if (params.page) qs.set('page', String(params.page));
  if (params.per_page) qs.set('per_page', String(params.per_page));
  return fetchJson(`${BASE}/join/${joinKey}?${qs}`);
}

export function fetchJoinPaths(entity?: string): Promise<{ joins: JoinPath[] }> {
  const qs = entity ? `?entity=${entity}` : '';
  return fetchJson(`${BASE}/joins/available${qs}`);
}
